import { Reducer, useCallback, useMemo, useReducer } from "react"

type State<IdType> = Map<IdType, number>

type Action<IdType> =
  | { type: 'registerId'; id: IdType; max?: number; }
  | { type: 'unregisterId'; id: IdType; max?: number; }

const ensureMaxInactiveIds = <IdType>(map: State<IdType>, max?: number) => {
  if (max === undefined) return map

  if (map.size <= max) {
    return map
  }

  let changed = false
  const newMap = new Map(map)
  for (const [id, count] of map) {
    if (newMap.size <= max) break

    if (count > 0) continue

    changed = true
    newMap.delete(id)
  }

  if (changed) {
    return newMap
  } else {
    return map
  }
}

function reducer<IdType> (state: State<IdType> = new Map(), action: Action<IdType>): State<IdType> {
  switch (action.type) {
    case 'registerId':
      if (state.has(action.id)) {
        return ensureMaxInactiveIds(new Map([...state.entries(), [action.id, state.get(action.id)! + 1]]), action.max)
      } else {
        return ensureMaxInactiveIds(new Map([...state.entries(), [action.id, 1]]), action.max)
      }
    case 'unregisterId':
      if (state.has(action.id)) {
        return ensureMaxInactiveIds(new Map(state).set(action.id, state.get(action.id)! - 1), action.max)
      } else {
        return state
      }
    default:
      throw new Error()
  }
}

const initialState = new Map()

export function useIdRegister<IdType> (max?: number) {
  const [registeredIds, dispatch] = useReducer<Reducer<State<IdType>, Action<IdType>>>(reducer, initialState)

  const ids = useMemo(() => Array.from(registeredIds.keys()), [registeredIds])

  const registerId = useCallback((id: IdType) => dispatch({ type: 'registerId', id, max }), [dispatch, max])
  const unregisterId = useCallback((id: IdType) => dispatch({ type: 'unregisterId', id, max }), [dispatch, max])

  return [ids, registerId, unregisterId] as const
}